import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';
import BRANDING from '../branding';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const isCartEnabled = BRANDING.features?.cart !== false;

    const fetchCart = useCallback(async () => {
        const token = getToken();
        if (!token || !isCartEnabled) return;

        try {
            setLoading(true);
            const res = await axios.get(`${CONFIG.API_URL}/api/cart`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCartItems(res.data);
        } catch (err) {
            console.error('Failed to fetch cart:', err);
        } finally {
            setLoading(false);
        }
    }, [isCartEnabled]);

    useEffect(() => {
        if (isCartEnabled) {
            const token = getToken();
            if (token) {
                fetchCart();
            } else {
                // Load from local storage for guests
                const localCart = localStorage.getItem('guest_cart');
                if (localCart) {
                    try {
                        setCartItems(JSON.parse(localCart));
                    } catch (e) {
                        setCartItems([]);
                    }
                }
            }
        }
    }, [fetchCart, isCartEnabled]);

    const addToCart = async (product, quantity = 1) => {
        const token = getToken();
        console.log('🛒 addToCart triggered:', { productId: product?.id, quantity, hasToken: !!token });
        if (token) {
            try {
                console.log('📡 Sending add-to-cart request...');
                const response = await axios.post(`${CONFIG.API_URL}/api/cart`, 
                    { productId: product.id, quantity },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('✅ Add to cart success:', response.data);
                await fetchCart();
                console.log('✅ Cart refreshed');
            } catch (err) {
                console.error('❌ Add to cart failed:', err.response?.data || err.message);
                console.error('Full error:', err);
            }
        } else {
            console.log('👤 Guest user - using localStorage');
            setCartItems(prev => {
                const existing = prev.find(item => item.product_id === product.id);
                let newCart;
                if (existing) {
                    newCart = prev.map(item => item.product_id === product.id ? { ...item, quantity: item.quantity + quantity } : item);
                    console.log('✅ Updated quantity for existing item');
                } else {
                    newCart = [...prev, { ...product, product_id: product.id, quantity }];
                    console.log('✅ Added new item to guest cart');
                }
                localStorage.setItem('guest_cart', JSON.stringify(newCart));
                return newCart;
            });
        }
    };

    const removeFromCart = async (cartItemId) => {
        const token = getToken();
        if (token) {
            try {
                await axios.delete(`${CONFIG.API_URL}/api/cart/${cartItemId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchCart();
            } catch (err) {
                console.error('Remove from cart failed:', err);
            }
        } else {
            setCartItems(prev => {
                const newCart = prev.filter(item => item.id !== cartItemId && item.product_id !== cartItemId);
                localStorage.setItem('guest_cart', JSON.stringify(newCart));
                return newCart;
            });
        }
    };

    const updateQuantity = async (cartItemId, quantity) => {
        if (quantity < 1) return;
        const token = getToken();
        if (token) {
            try {
                await axios.put(`${CONFIG.API_URL}/api/cart/${cartItemId}`, 
                    { quantity },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                fetchCart();
            } catch (err) {
                console.error('Update quantity failed:', err);
            }
        } else {
            setCartItems(prev => {
                const newCart = prev.map(item => (item.id === cartItemId || item.product_id === cartItemId) ? { ...item, quantity } : item);
                localStorage.setItem('guest_cart', JSON.stringify(newCart));
                return newCart;
            });
        }
    };

    const clearCart = async () => {
        const token = getToken();
        if (token) {
            try {
                await axios.delete(`${CONFIG.API_URL}/api/cart`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCartItems([]);
            } catch (err) {
                console.error('Clear cart failed:', err);
            }
        } else {
            setCartItems([]);
            localStorage.removeItem('guest_cart');
        }
    };

    const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <CartContext.Provider value={{ 
            cartItems, 
            addToCart, 
            removeFromCart, 
            updateQuantity, 
            clearCart, 
            cartCount,
            loading,
            refreshCart: fetchCart 
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
